/**
 * Detects if a color is dark and sets appropriate contrast colors as CSS variables
 */
export const setContrastColors = () => {
	// Simple helper to get RGB values from any CSS color
	const getRGB = (color) => {
		const el = document.createElement('div');
		el.style.color = color;
		document.body.appendChild(el);
		const computed = getComputedStyle(el).color;
		document.body.removeChild(el);
		
		const match = computed.match(/(\d+), (\d+), (\d+)/);
		return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : [0, 0, 0];
	};
	
	// Calculate perceived brightness using WCAG formula
	const getBrightness = (r, g, b) => (r * 299 + g * 587 + b * 114) / 1000;
	
	// Get colors from CSS variables and set contrast values
	const processColors = (colorVars) => {
		const root = document.documentElement;
		const style = getComputedStyle(root);
		
		colorVars.forEach(varName => {
			const color = style.getPropertyValue(varName).trim();
			if (!color) return;
			
			// Get RGB and calculate brightness
			const [r, g, b] = getRGB(color);
			const brightness = getBrightness(r, g, b);
			const isDark = brightness < 128;
			
			// Set contrast color as CSS variable
			root.style.setProperty(`${varName}-contrast`, isDark ? '#fff' : '#000');
			root.style.setProperty(`${varName}-is-dark`, isDark ? '1' : '0');
		});
	};
	
	// Process Bootstrap and custom theme colors
	const colorVars = [
		'--bs-primary',
		'--bs-secondary',
		'--bs-success',
		'--bs-danger',
		'--bs-warning',
		'--bs-info',
		'--bs-dark',
		'--bs-light',
		'--bs-primary-darken',
		'--bs-secondary-darken',
		'--bs-primary-lighten'
	];
	
	if (typeof window !== 'undefined') {
		// Process on load and when CSS variables might change
		processColors(colorVars);
		window.addEventListener('load', () => processColors(colorVars));
		document.addEventListener('themeChanged', () => processColors(colorVars));
	}
};

/**
 * Simple helper to check if a color is dark
 * @param {string} colorVar - CSS variable name (e.g. '--bs-primary')
 * @returns {boolean} True if color is dark 
 */
export const isColorDark = (colorVar) => {
	if (typeof window === 'undefined') return false;
	
	const isDarkValue = getComputedStyle(document.documentElement)
		.getPropertyValue(`${colorVar}-is-dark`)
		.trim();
		
	return isDarkValue === '1';
};

// Initialize color contrast variables
if (typeof window !== 'undefined') {
	setContrastColors();
}
